import { MerchantPerson } from 'APIs/merchant.persons/merchant.persons.types';
import Spinner from 'Components/Spinner/Spinner';
import { FunctionComponent, useMemo } from 'react';
import DataTable, { TableColumn, createTheme } from 'react-data-table-component';
import { FaHourglassHalf } from 'react-icons/fa';
import { FaRegEdit } from 'react-icons/fa';
import { RiDeleteBin2Line } from 'react-icons/ri';

import EmptyComponent from './EmptyComponent';
import ErrorComponent from './ErrorComponent';
import { TableStyleSettings, TableThemeSettings } from './constants';
import { ActionButton, ActionWaiting, MessageWrapper, PersonDataTableContainer, ViewFileButton } from './styles';

createTheme('polarized', TableThemeSettings);

interface Props {
  personList?: Array<MerchantPerson>;
  fetchListLoader: boolean;
  isErrorRequest: boolean;
  actionPersons: Array<string>;
  handleEditPerson: (ownerId: string) => void;
  handleDeletePerson: (ownerId: string) => void;
  handleFileClick: (e: React.MouseEvent<HTMLButtonElement>, fileId: string) => void;
}

function PersonDataTable({
  personList,
  fetchListLoader,
  isErrorRequest,
  actionPersons,
  handleEditPerson,
  handleDeletePerson,
  handleFileClick,
}: Props) {
  const HourGlassIcon = FaHourglassHalf as FunctionComponent;
  const EditIcon = FaRegEdit as FunctionComponent;
  const DeleteIcon = RiDeleteBin2Line as FunctionComponent;

  const columns: TableColumn<MerchantPerson>[] = useMemo(
    () => [
      {
        name: 'Name',
        cell: (row: MerchantPerson) => `${row.profile.givenName} ${row.profile.familyName}`,
        grow: 5,
      },
      {
        name: 'Roles',
        selector: (row: MerchantPerson) => row.profile.roles.join(', '),
        grow: 1,
      },
      {
        name: 'ID type',
        selector: (row: MerchantPerson) => row.profile.verificationDocs?.idType,
        grow: 1,
      },
      {
        name: 'Verification status',
        selector: (row: MerchantPerson) => row.verificationResult.status,
        grow: 1,
      },
      {
        name: 'Verification errors',
        selector: (row: MerchantPerson) => row.verificationResult.details?.join(', '),
        grow: 1,
      },
      {
        name: 'Deletable',
        selector: (row: MerchantPerson) => (row.isDeletable ? 'Yes' : 'No'),
        grow: 1,
      },
      {
        name: 'Doc (Front)',
        cell: (row: MerchantPerson) =>
          row?.profile?.verificationDocs?.docFrontFileId ? (
            <ViewFileButton onClick={(e) => handleFileClick(e, row.profile?.verificationDocs?.docFrontFileId!)}>
              {row.profile?.verificationDocs?.docFrontFileId!}
            </ViewFileButton>
          ) : (
            ''
          ),
        grow: 2,
      },
      {
        name: 'Doc (Back)',
        cell: (row: MerchantPerson) =>
          row?.profile?.verificationDocs?.docBackFileId ? (
            <ViewFileButton onClick={(e) => handleFileClick(e, row.profile?.verificationDocs?.docBackFileId!)}>
              {row.profile?.verificationDocs?.docBackFileId!}
            </ViewFileButton>
          ) : (
            ''
          ),
        grow: 2,
      },
      {
        name: '',
        cell: (row: MerchantPerson) => (
          <div>
            {actionPersons && actionPersons.includes(row.ownerId) ? (
              <ActionWaiting>
                <HourGlassIcon />
              </ActionWaiting>
            ) : (
              <div className="hover-button-wrapper">
                <ActionButton className="hover-button" title="Edit" onClick={() => handleEditPerson(row.ownerId)}>
                  <EditIcon />
                </ActionButton>
                <ActionButton
                  className="hover-button"
                  title="Resend email"
                  onClick={() => handleDeletePerson(row.ownerId)}
                >
                  <DeleteIcon />
                </ActionButton>
              </div>
            )}
          </div>
        ),
        grow: 1,
        center: true,
      },
    ],
    [DeleteIcon, EditIcon, HourGlassIcon, actionPersons, handleDeletePerson, handleEditPerson, handleFileClick],
  );

  return (
    <PersonDataTableContainer>
      <DataTable
        pagination={false}
        responsive
        columns={columns}
        data={personList ?? []}
        customStyles={TableStyleSettings}
        theme="polarized"
        className=""
        progressPending={fetchListLoader}
        progressComponent={
          <MessageWrapper>
            <Spinner color="#0016D1" height="25px" width="25px" />
          </MessageWrapper>
        }
        noDataComponent={
          isErrorRequest ? (
            <MessageWrapper>
              <ErrorComponent />
            </MessageWrapper>
          ) : (
            <MessageWrapper>
              <EmptyComponent />
            </MessageWrapper>
          )
        }
        paginationServer
        sortServer
        persistTableHead
        defaultSortAsc={false}
      />
    </PersonDataTableContainer>
  );
}

export default PersonDataTable;
